/**
 * TriageCard — hero triage card primitive for the home page above-the-fold slot.
 * Three modes: park (best attraction recommendation), planning (next trip),
 * empty (no trips yet).
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { WaitTimeIndicator } from './WaitTimeIndicator';

// Park mode: show best attraction recommendation
export interface ParkModeCardProps {
  mode: 'park';
  attractionName: string;
  waitMinutes: number | null;
  attractionId?: string;
  visitId: string;
}

// Planning mode: show next upcoming trip
export interface PlanningModeCardProps {
  mode: 'planning';
  tripName: string;
  startDate: string;
  endDate: string;
  daysUntilStart: number;
  visitCount: number;
  tripId: string;
}

// Empty state: no trips at all
export interface EmptyStateCardProps {
  mode: 'empty';
}

export type TriageCardProps = ParkModeCardProps | PlanningModeCardProps | EmptyStateCardProps;

function formatShortDate(dateStr: string): string {
  const date = new Date(dateStr + 'T00:00:00');
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function ParkCard({ attractionName, waitMinutes, attractionId, visitId }: ParkModeCardProps): React.ReactElement {
  const navigate = useNavigate();
  return (
    <div className="w-full bg-white dark:bg-surface-dark-card rounded-2xl p-5 shadow-elevated border-l-4 border-emerald-500 dark:ring-1 dark:ring-white/[0.08]">
      <p className="text-[10px] font-semibold text-emerald-600 dark:text-emerald-400 uppercase tracking-widest">Go Now</p>
      <p className="text-3xl font-bold text-gray-900 dark:text-gray-100 mt-1 leading-tight">{attractionName}</p>
      <div className="mt-3">
        <WaitTimeIndicator minutes={waitMinutes} attractionId={attractionId} />
      </div>
      <div className="mt-5">
        <button
          type="button"
          className="bg-emerald-500 text-white rounded-lg px-4 py-2 text-sm font-semibold hover:bg-emerald-600 focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-colors"
          onClick={() => navigate('/visit/' + visitId)}
        >
          View Visit →
        </button>
      </div>
    </div>
  );
}

function PlanningCard({ tripName, startDate, endDate, daysUntilStart, visitCount, tripId }: PlanningModeCardProps): React.ReactElement {
  const navigate = useNavigate();
  return (
    <div className="w-full bg-white dark:bg-surface-dark-card rounded-2xl p-5 shadow-elevated border-l-4 border-disney-blue-500 dark:ring-1 dark:ring-white/[0.08]">
      <p className="text-[10px] font-semibold text-disney-blue-500 dark:text-disney-blue-400 uppercase tracking-widest">Next Trip</p>
      <p className="text-3xl font-bold text-gray-900 dark:text-gray-100 mt-1 leading-tight">{tripName}</p>
      <div className="mt-2 flex items-center gap-3">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {formatShortDate(startDate)} — {formatShortDate(endDate)}
        </p>
        {daysUntilStart > 0 && (
          <span className="text-xs font-semibold bg-disney-blue-50 dark:bg-disney-blue-900/30 text-disney-blue-600 dark:text-disney-blue-400 px-2 py-0.5 rounded-full">
            {daysUntilStart}d away
          </span>
        )}
        {daysUntilStart === 0 && (
          <span className="text-xs font-semibold bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 px-2 py-0.5 rounded-full">
            Today!
          </span>
        )}
      </div>
      <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{visitCount} {visitCount === 1 ? 'visit' : 'visits'} planned</p>
      <div className="mt-5">
        <button
          type="button"
          className="bg-disney-blue-500 text-white rounded-lg px-4 py-2 text-sm font-semibold hover:bg-disney-blue-600 focus:outline-none focus:ring-2 focus:ring-disney-blue-500 transition-colors"
          onClick={() => navigate('/trip/' + tripId, { state: { from: 'home' } })}
        >
          View Trip →
        </button>
      </div>
    </div>
  );
}

function EmptyCard(): React.ReactElement {
  const navigate = useNavigate();
  return (
    <div className="w-full bg-white dark:bg-surface-dark-card rounded-xl p-6 shadow-card text-center dark:ring-1 dark:ring-white/[0.06]">
      <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">Welcome to Disney Queue Tracker</p>
      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Create your first trip to get started</p>
      <div className="mt-4">
        <button
          type="button"
          className="bg-disney-blue-500 text-white rounded-lg px-4 py-2 text-sm font-medium hover:bg-disney-blue-600 focus:outline-none focus:ring-2 focus:ring-disney-blue-500"
          onClick={() => navigate('/create')}
        >
          Create your first trip
        </button>
      </div>
    </div>
  );
}

export function TriageCard(props: TriageCardProps): React.ReactElement {
  if (props.mode === 'park') {
    return <ParkCard {...props} />;
  }
  if (props.mode === 'planning') {
    return <PlanningCard {...props} />;
  }
  return <EmptyCard />;
}
